import type { UpdateTruckInput } from '@chukua/common';
import { Alert, Button, Form, Input } from 'antd';
import { useEffect } from 'react';
import type { useTruckDevice } from '../../../../integrations/features/gps/public/use-truck-device';
import { splitDevice, type WithDevice } from '../policies/truck-form-device.policy';
import type { TruckClass } from '../../../../reference-data/reference-data.api';
import { TruckCapabilityFields } from './TruckCapabilityFields';
import { TruckParticularsFields } from './TruckParticularsFields';
import type { TruckView } from '../trucks.api';

export interface TruckDetailsFormProps {
  truck: TruckView;
  truckClasses: TruckClass[];
  canManage: boolean;
  error: string | null;
  saving: boolean;
  /** The device on the truck now (`current`, undefined while it loads) and its picker value (`choice`). */
  device: Pick<ReturnType<typeof useTruckDevice>, 'current' | 'choice'>;
  onSave: (input: UpdateTruckInput, device: string | undefined) => Promise<boolean>;
}

function toFormValues(truck: TruckView, deviceChoice: string | undefined): WithDevice<UpdateTruckInput> {
  return {
    registration: truck.registration,
    classCode: truck.classCode,
    make: truck.make,
    model: truck.model,
    year: truck.year,
    vin: truck.vin,
    engineNumber: truck.engineNumber,
    capacityTonnes: truck.capacityTonnes,
    tankCapacityLitres: truck.tankCapacityLitres,
    hasGenset: truck.hasGenset,
    isBonded: truck.isBonded,
    isHazardousCertified: truck.isHazardousCertified,
    isCrossBorderEquipped: truck.isCrossBorderEquipped,
    expectedKmPerLitre: truck.expectedKmPerLitre,
    color: truck.color,
    device: deviceChoice,
  };
}

/** Every field but the odometer, editable inline for `truck.manage` holders — read-only for everyone else with `fleet.view` (CLAUDE.md: the server remains the authority). */
export function TruckDetailsForm({ truck, truckClasses, canManage, error, saving, device, onSave }: TruckDetailsFormProps) {
  const [form] = Form.useForm<WithDevice<UpdateTruckInput>>();

  useEffect(() => {
    form.setFieldsValue(toFormValues(truck, device.choice));
  }, [truck, device.choice, form]);

  async function submit(values: WithDevice<UpdateTruckInput>) {
    const split = splitDevice(values);
    await onSave(split.input, split.device);
  }

  return (
    <Form<WithDevice<UpdateTruckInput>>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={submit}
      disabled={!canManage || saving}
      initialValues={toFormValues(truck, device.choice)}
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form.Item name="registration" label="Registration" rules={[{ required: true, message: 'Enter a registration' }]}>
        <Input className="font-mono uppercase" />
      </Form.Item>
      <TruckParticularsFields truckClasses={truckClasses} truckId={truck.id} currentDevice={device.current} />
      <TruckCapabilityFields />
      {canManage && (
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save changes
          </Button>
        </Form.Item>
      )}
    </Form>
  );
}
